/**
 * Unit conversion at the UI boundary. The model and engine are SI; inputs and displays use the units
 * engineers write. Parsing accepts a unit suffix ("6 barg", "600 kPa", "2.5 mm2") and a decimal comma.
 */

export type Quantity =
  | 'pressureAbs'
  | 'pressureDiff'
  | 'temperature'
  | 'holeArea'
  | 'area'
  | 'airFlow'
  | 'velocity'
  | 'massRate'
  | 'length'
  | 'fraction'
  | 'percent'
  | 'molarMass'
  | 'density'
  | 'dimensionless'
  | 'cp'
  | 'pressurePlain'
  | 'celsius';

interface UnitDef {
  label: string;
  toSi: (v: number) => number;
  fromSi: (v: number) => number;
}

const ATM = 101_325;
const lin = (label: string, k: number, offset = 0): UnitDef => ({ label, toSi: (v) => v * k + offset, fromSi: (v) => (v - offset) / k });

export const UNITS: Record<Quantity, UnitDef[]> = {
  /** Absolute pressure stored; gauge by default for display. */
  pressureAbs: [lin('barg', 1e5, ATM), lin('bara', 1e5), lin('kPa(g)', 1e3, ATM), lin('kPa(a)', 1e3), lin('Pa(a)', 1)],
  pressureDiff: [lin('bar', 1e5), lin('kPa', 1e3), lin('Pa', 1)],
  pressurePlain: [lin('kPa', 1e3), lin('Pa', 1), lin('bar', 1e5)],
  temperature: [lin('°C', 1, 273.15), lin('K', 1)],
  holeArea: [lin('mm²', 1e-6), lin('m²', 1)],
  area: [lin('m²', 1)],
  airFlow: [lin('m³/h', 1 / 3600), lin('m³/s', 1)],
  velocity: [lin('m/s', 1)],
  massRate: [lin('kg/s', 1), lin('g/s', 1e-3), lin('kg/h', 1 / 3600)],
  length: [lin('m', 1), lin('mm', 1e-3)],
  fraction: [lin('%', 0.01), lin('', 1)],
  percent: [lin('% vol', 0.01), lin('vol/vol', 1)],
  molarMass: [lin('kg/kmol', 1)],
  density: [lin('kg/m³', 1)],
  dimensionless: [lin('', 1)],
  cp: [lin('J/(kg·K)', 1)],
  /** A temperature stored in °C (AIT, flash point). */
  celsius: [lin('°C', 1)],
};

const ALIASES: Record<string, string> = {
  mm2: 'mm²', m2: 'm²', 'm3/h': 'm³/h', 'm3/s': 'm³/s', c: '°C', degc: '°C', '°c': '°C', k: 'K', kpa: 'kPa', pa: 'Pa',
  'kpag': 'kPa(g)', 'kpa(g)': 'kPa(g)', 'kpaa': 'kPa(a)', 'kpa(a)': 'kPa(a)', 'kg/m3': 'kg/m³', barg: 'barg', bara: 'bara', bar: 'bar',
  'g/s': 'g/s', 'kg/s': 'kg/s', 'kg/h': 'kg/h', mm: 'mm', m: 'm', '%': '%', 'm/s': 'm/s', 'vol%': '% vol', '%vol': '% vol',
};

export interface Parsed {
  si: number;
  unitLabel: string;
}

/**
 * Parses user input for a quantity. Without a unit, `defaultUnit` applies. Returns an error message
 * when the text can't be read; never clamps or guesses.
 */
export function parseQuantity(text: string, quantity: Quantity, defaultUnit: string): Parsed | { error: string } {
  const t = text.trim().replace(/\s+/g, ' ');
  if (!t) return { error: 'Enter a value.' };
  const m = t.match(/^([-+]?(?:\d+(?:[.,]\d*)?|[.,]\d+)(?:e[-+]?\d+)?)\s*(.*)$/i);
  if (!m) return { error: `"${text}" is not a number.` };
  const num = Number(m[1]!.replace(',', '.'));
  if (!Number.isFinite(num)) return { error: `"${text}" is not a number.` };
  const rawUnit = (m[2] ?? '').trim();
  const defs = UNITS[quantity];
  let def = defs.find((d) => d.label === defaultUnit) ?? defs[0]!;
  if (rawUnit) {
    const key = rawUnit.toLowerCase().replace(/\s/g, '');
    const label = ALIASES[key] ?? rawUnit;
    const found = defs.find((d) => d.label === label || d.label.toLowerCase().replace(/\s/g, '') === key);
    if (!found) return { error: `${rawUnit} isn't a unit for this field. Use ${defs.map((d) => d.label).filter(Boolean).join(', ')}.` };
    def = found;
  }
  return { si: def.toSi(num), unitLabel: def.label };
}

export function fromSi(si: number, quantity: Quantity, unit: string): number {
  const def = UNITS[quantity].find((d) => d.label === unit) ?? UNITS[quantity][0]!;
  return def.fromSi(si);
}
